export interface HighlightEvent {
  imageUrl: string;
  eventUrl: string;
}

export interface UpcomingEvent {
  eventId: string;
  thumbnailUrl: string;
  eventName: string;
  date: string;
  location: string;
  eventUrl: string;
  imageUrl: string;
}

export interface HighlightEventsResponse {
  events: HighlightEvent[];
}

export interface UpcomingEventsResponse {
  events: UpcomingEvent[];
}

const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;

class ApiService {
  private async fetchWithMultipleStrategies<T>(endpoint: string): Promise<T> {
    const fullUrl = `${API_BASE_URL}${endpoint}`;

    const strategies = [
      {
        name: 'Standard CORS',
        config: {
          method: 'GET',
          mode: 'cors' as RequestMode,
          credentials: 'omit' as RequestCredentials,
          headers: {
            'Accept': 'application/json',
          },
        }
      },
      {
        name: 'No-CORS Mode',
        config: {
          method: 'GET',
          mode: 'no-cors' as RequestMode,
          credentials: 'omit' as RequestCredentials,
        }
      },
      {
        name: 'Same-Origin',
        config: {
          method: 'GET',
          mode: 'same-origin' as RequestMode,
          credentials: 'omit' as RequestCredentials,
          headers: {
            'Accept': 'application/json',
          },
        }
      },
      {
        name: 'Basic Fetch',
        config: {
          method: 'GET',
        }
      }
    ];

    for (const strategy of strategies) {
      try {
        const response = await Promise.race([
          fetch(fullUrl, strategy.config),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 8000)
          )
        ]);

        if (response.type === 'opaque') {
          throw new Error('Opaque response - trying next strategy');
        }

        if (!response.ok) {
          continue;
        }

        const data = await response.json();
        return data;

      } catch {
        continue;
      }
    }

    try {
      const jsonpResult = await this.tryJsonpApproach(endpoint);
      if (jsonpResult) {
        return jsonpResult;
      }
    } catch {}

    throw new Error(`All API strategies failed for ${fullUrl}.`);
  }

  private async tryJsonpApproach<T>(endpoint: string): Promise<T | null> {
    return new Promise((resolve, reject) => {
      const callbackName = `jsonp_callback_${Date.now()}`;
      const script = document.createElement('script');

      (window as any)[callbackName] = (data: T) => {
        delete (window as any)[callbackName];
        document.head.removeChild(script);
        resolve(data);
      };

      script.onerror = () => {
        delete (window as any)[callbackName];
        document.head.removeChild(script);
        reject(new Error('JSONP request failed'));
      };

      script.src = `${API_BASE_URL}${endpoint}&callback=${callbackName}`;
      document.head.appendChild(script);

      setTimeout(() => {
        if ((window as any)[callbackName]) {
          delete (window as any)[callbackName];
          document.head.removeChild(script);
          reject(new Error('JSONP timeout'));
        }
      }, 10000);
    });
  }

  async getHighlightEvents(): Promise<HighlightEventsResponse> {
    const response = await this.fetchWithMultipleStrategies<HighlightEventsResponse>('/events?type=highlights');
    return response;
  }

  async getUpcomingEvents(): Promise<UpcomingEventsResponse> {
    const response = await this.fetchWithMultipleStrategies<UpcomingEventsResponse>('/events?type=upcoming');
    return response;
  }

  /**
   * Extract the event name from the event URL
   * @param eventUrl Full event URL
   * @returns Cleaned event name
   */
  static extractEventNameFromUrl(eventUrl: string): string {
    try {
      const url = new URL(eventUrl);
      const pathParts = url.pathname.split('/');
      const eventSlug = pathParts[pathParts.length - 2] || pathParts[pathParts.length - 1];
      const eventName = eventSlug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      return eventName
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 50);
    } catch {
      return 'Evento';
    }
  }

  /**
   * Parse date string into date and time
   * @param dateString Formatted string from API
   * @returns Object with `date` and `time`
   */
  static parseEventDate(dateString: string): { date: string; time: string } {
    try {
      const parts = dateString.split(' às ');
      const datePart = parts[0];
      const timePart = parts[1]?.replace('h', '') || '20:00';

      return {
        date: datePart,
        time: timePart,
      };
    } catch {
      return {
        date: dateString,
        time: '20:00',
      };
    }
  }

  /**
   * Clean up location string for display
   * @param location Raw location from API
   * @returns Cleaned location
   */
  static cleanLocation(location: string): string {
    return location.split(',')[0].trim();
  }

  /**
   * Perform a test fetch to check API connectivity
   * @returns True if API responds correctly
   */
  static async testApiConnectivity(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/events?type=highlights`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  static get isUsingLiveApi(): boolean {
    return true;
  }

  static get environmentInfo() {
    return {
      isUsingLiveApi: this.isUsingLiveApi,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
    };
  }
}

export const apiService = new ApiService();
export { ApiService };
